// src/common/Documents/ActionButtons/ActionButtons.js

import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import {
  Menu,
  TextField,
  Pagination,
  List,
  ListItem,
  ListItemText,
  Box,
  Divider,
  // Import CircularProgress for loading indicator
  CircularProgress,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useLanguage } from "context/LanguageContext";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const ActionButtons = ({
  onRecognize,
  onRecognizeUnrecognized,
  onRecognizeHeaders,
  onDeleteSelected,
  onExportXml,
  onExportExcel,
  onExportRoda,
  onUpload,
  uploading,
  uploadProgress,
  onGeneratePdfReport, // {{ Add onGeneratePdfReport prop }}
  isLoading, // {{ Add isLoading prop for PDF generation }}
}) => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [recognizeAnchorEl, setRecognizeAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const exportOptions = useMemo(
    () => [
      { label: t("exportToAgnum"), value: "agnum" },
      { label: t("exportExcel"), value: "excel" },
      { label: t("exportRodaTxt") || "Export RODA TXT", value: "roda" },
      { label: t("generatePdfReport"), value: "pdf" },
    ],
    [t]
  );

  const recognizeOptions = useMemo(
    () => [
      { label: t("recognize"), action: onRecognize },
      { label: t("recognizeUnrecognized"), action: onRecognizeUnrecognized },
      { label: t("recognizeHeaders"), action: onRecognizeHeaders },
    ],
    [t, onRecognize, onRecognizeUnrecognized, onRecognizeHeaders]
  );

  const filteredOptions = useMemo(() => {
    return exportOptions.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [exportOptions, searchQuery]);

  const totalPages = Math.ceil(filteredOptions.length / itemsPerPage);

  const displayedOptions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredOptions.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredOptions, currentPage, itemsPerPage]);

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleRecognizeClick = (event) => {
    setRecognizeAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleRecognizeClose = () => {
    setRecognizeAnchorEl(null);
  };

  const handleOptionSelect = (value) => {
    console.log("Selected export type:", value);
    if (value === "excel") {
      onExportExcel();
    } else if (value === "roda") {
      if (onExportRoda) onExportRoda();
    } else if (value === "pdf") {
      if (onGeneratePdfReport) onGeneratePdfReport();
    } else {
      onExportXml(value, exportWithMatches);
    }
    handleExportClose();
  };

  const handleRecognizeSelect = (action) => {
    action();
    handleRecognizeClose();
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Removed: toggle handler moved inline in menu

  const [exportWithMatches, setExportWithMatches] = useState(false);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: isMdDown ? "repeat(2, minmax(0, 1fr))" : "none",
        gap: 0.75,
        marginBottom: isMdDown ? 1 : 0,
        width: isMdDown ? "100%" : "auto",
        ...(isMdDown
          ? {}
          : {
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              rowGap: 0.75,
              columnGap: 0.75,
            }),
      }}
    >
      {/* Atpažinimo mygtukas su išskleidžiamuoju meniu */}
      <Box sx={{ width: isMdDown ? "100%" : "auto" }}>
        <MDButton
          variant="outlined"
          color="info"
          size="small"
          onClick={handleRecognizeClick}
          endIcon={<ArrowDropDownIcon />}
          sx={{ width: isMdDown ? "100%" : "auto", whiteSpace: "nowrap", minWidth: 0 }}
        >
          {t("recognize")}
        </MDButton>
        <Menu
          anchorEl={recognizeAnchorEl}
          open={Boolean(recognizeAnchorEl)}
          onClose={handleRecognizeClose}
        >
          <List>
            {recognizeOptions.map((option, index) => (
              <ListItem button key={index} onClick={() => handleRecognizeSelect(option.action)}>
                <ListItemText primary={option.label} />
              </ListItem>
            ))}
          </List>
        </Menu>
      </Box>

      <MDButton
        variant="outlined"
        color="error"
        size="small"
        onClick={onDeleteSelected}
        sx={{ width: isMdDown ? "100%" : "auto", whiteSpace: "nowrap", minWidth: 0 }}
      >
        {t("deleteSelected")}
      </MDButton>

      {/* Export mygtukas su išskleidžiamuoju meniu */}
      <Box sx={{ width: isMdDown ? "100%" : "auto" }}>
        <MDButton
          variant="outlined"
          color="info"
          size="small"
          onClick={handleExportClick}
          endIcon={<ArrowDropDownIcon />}
          disabled={isLoading}
          sx={{ width: isMdDown ? "100%" : "auto", whiteSpace: "nowrap", minWidth: 0 }}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              {t("generating")}
            </>
          ) : (
            t("export")
          )}
        </MDButton>
        <Menu
          anchorEl={exportAnchorEl}
          open={Boolean(exportAnchorEl)}
          onClose={handleExportClose}
          PaperProps={{
            style: { width: 300 },
          }}
        >
          {/* Paieškos laukas */}
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t("search")}
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={exportWithMatches}
                  onChange={(e) => setExportWithMatches(e.target.checked)}
                  name="exportWithMatches"
                  color="primary"
                />
              }
              label={t("exportWithMatches")}
            />
          </Box>
          <Divider />

          {/* Eksporto parinkčių sąrašas */}
          <List>
            {displayedOptions.map((option) => (
              <ListItem button key={option.value} onClick={() => handleOptionSelect(option.value)}>
                <ListItemText primary={option.label} />
              </ListItem>
            ))}
          </List>
          <Divider />

          {/* Eksporto parinkčių puslapiavimas */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                size="small"
                color="primary"
              />
            </Box>
          )}
        </Menu>
      </Box>

      {/* Failų įkėlimo iš galerijos / failų sistemos mygtukas */}
      <MDButton
        variant="outlined"
        color="info"
        size="small"
        component="label"
        disabled={!!uploading}
        sx={{ width: isMdDown ? "100%" : "auto", whiteSpace: "nowrap", minWidth: 0 }}
      >
        {uploading ? (
          <>
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            {t("uploading") || "Uploading"}
            {typeof uploadProgress === "number" ? ` ${uploadProgress}%` : ""}
          </>
        ) : (
          t("uploadFromGallery") || "Upload from Gallery"
        )}
        <input
          type="file"
          multiple
          accept="image/*,.pdf,application/pdf"
          hidden
          onChange={onUpload}
          disabled={!!uploading}
        />
      </MDButton>

      {/* Fotografavimo per telefono kamerą mygtukas */}
      <MDButton
        variant="outlined"
        color="info"
        size="small"
        component="label"
        disabled={!!uploading}
        sx={{ width: isMdDown ? "100%" : "auto", whiteSpace: "nowrap", minWidth: 0 }}
      >
        {uploading ? (
          <>
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            {t("uploading") || "Uploading"}
            {typeof uploadProgress === "number" ? ` ${uploadProgress}%` : ""}
          </>
        ) : (
          t("takePhoto") || "Take Photo"
        )}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          hidden
          onChange={onUpload}
          disabled={!!uploading}
        />
      </MDButton>
    </Box>
  );
};

ActionButtons.propTypes = {
  onRecognize: PropTypes.func.isRequired,
  onRecognizeUnrecognized: PropTypes.func.isRequired,
  onRecognizeHeaders: PropTypes.func.isRequired,
  onDeleteSelected: PropTypes.func.isRequired,
  onExportXml: PropTypes.func.isRequired,
  onExportExcel: PropTypes.func.isRequired,
  onExportRoda: PropTypes.func,
  onUpload: PropTypes.func.isRequired,
  uploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  onGeneratePdfReport: PropTypes.func, // {{ Add onGeneratePdfReport prop type }}
  isLoading: PropTypes.bool, // {{ Add isLoading prop type }}
};

// Set default value for isLoading
ActionButtons.defaultProps = {
  isLoading: false,
  uploading: false,
  uploadProgress: undefined,
};

export default ActionButtons;
